import { useState, useEffect, useCallback } from 'react'
import billingApi from '@/lib/billingApi'

export interface BillingStatus {
  status: 'active' | 'past_due' | 'suspended' | 'banned'
  past_due_since: string | null
  grace_period_ends: string | null
  amount_due: number | null
  days_until_deletion: number | null
  is_locked_out: boolean
  enforcement_state: 'past_due' | 'suspended' | 'services_deleted' | null
  power_off_at: string | null
  deletion_at: string | null
  backup_deletion_deadline?: string | null
  hold_until: string | null
  affected_server_count: number
  restoration_possible: boolean
}

interface UseBillingStatusResult {
  billingStatus: BillingStatus | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Hook to check the current user's billing status.
 * Used for lockout functionality when payments are overdue.
 */
export function useBillingStatus(enabled = true): UseBillingStatusResult {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    if (!enabled) {
      setBillingStatus(null)
      setLoading(false)
      setError(null)
      return
    }
    // Skip fetch when no auth token is present — avoids unnecessary 404/401 on unauthenticated loads
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    if (!token) {
      setBillingStatus({
        status: 'active',
        past_due_since: null,
        grace_period_ends: null,
        amount_due: null,
        days_until_deletion: null,
        is_locked_out: false,
        enforcement_state: null,
        power_off_at: null,
        deletion_at: null,
        hold_until: null,
        affected_server_count: 0,
        restoration_possible: true
      })
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      // Route through /api/ nginx proxy so Hoard receives the full /billing/status path
      const response = await billingApi.get('/api/billing/status')
      setBillingStatus(response.data.data || response.data)
    } catch (err: any) {
      // Don't treat 401/403/404 as errors - user might not be fully authenticated
      // or the billing service might be temporarily unreachable
      if ([401, 403, 404].includes(err.response?.status)) {
        setBillingStatus({
          status: 'active',
          past_due_since: null,
          grace_period_ends: null,
          amount_due: null,
          days_until_deletion: null,
          is_locked_out: false,
          enforcement_state: null,
          power_off_at: null,
          deletion_at: null,
          hold_until: null,
          affected_server_count: 0,
          restoration_possible: true
        })
      } else {
        setError(err.response?.data?.error || 'Failed to check billing status')
      }
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    void fetchStatus()
    if (!enabled) return
    
    // Refresh every 5 minutes while the page is open
    const interval = setInterval(fetchStatus, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [enabled, fetchStatus])

  return {
    billingStatus,
    loading,
    error,
    refresh: fetchStatus
  }
}
